import { useState, type ReactElement } from 'react';

import { useSession } from '../access/session';
import { apiUrl } from '../lib/build';
import { isReport, reportHref, showStatDock } from './route';
import { useScreen } from './store';
import { byBook, dayKey, inMonth, isSample, moneyLine, rowDay, tapeLabel, tally, type ReportRow } from './report';
import './report.css';

const TONES = [
  'oklch(0.97 0.004 75)',
  'oklch(0.78 0.012 80)',
  'oklch(0.64 0.02 70)',
  'oklch(0.5 0.015 60)',
  'oklch(0.38 0.01 50)',
];

function moneyOf(rows: ReportRow[]): string {
  const plus = tally(rows, 'all', Date.now()).plus[0];
  return plus ? moneyLine(plus.amount, plus.cur) : '—';
}

function DockPie({ parts }: { parts: { name: string; n: number }[] }): ReactElement | null {
  if (parts.length === 0) return null;
  const total = parts.reduce((sum, part) => sum + part.n, 0) || 1;
  const radius = 46;
  const center = 58;
  let angle = -Math.PI / 2;
  const arcs = parts.map((part, index) => {
    const sweep = (part.n / total) * Math.PI * 2;
    const start = angle;
    angle += sweep;
    const end = angle - 0.0001;
    const x0 = center + radius * Math.cos(start);
    const y0 = center + radius * Math.sin(start);
    const x1 = center + radius * Math.cos(end);
    const y1 = center + radius * Math.sin(end);
    return {
      ...part,
      tone: TONES[index % TONES.length] ?? TONES[0],
      d: `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${radius} ${radius} 0 ${sweep > Math.PI ? 1 : 0} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`,
    };
  });
  return (
    <div className="nx-rep__dockpie">
      <svg viewBox="0 0 116 116" role="img" aria-label="конторы">
        {parts.length === 1 ? (
          <circle cx={center} cy={center} r={radius} fill="none" stroke={TONES[0]} strokeWidth="16" />
        ) : (
          arcs.map((arc) => <path key={arc.name} d={arc.d} fill="none" stroke={arc.tone} strokeWidth="16" />)
        )}
      </svg>
      <ul>
        {arcs.map((arc) => (
          <li key={arc.name}><i style={{ background: arc.tone }} /><span>{arc.name}</span><em>{arc.n}</em></li>
        ))}
      </ul>
    </div>
  );
}

/** Значок статистики в углу. На странице входа и на табло гонки его нет. */
export function StatDock(): ReactElement | null {
  const entered = Boolean(useSession((s) => s.me.id));
  const matchOpen = useScreen((s) => s.open !== null || s.popped.size > 0);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<ReportRow[] | null>(null);
  if (!entered || matchOpen || !showStatDock(location.pathname)) return null;
  const here = isReport(location.pathname);
  const show = (): void => {
    const next = !open;
    setOpen(next);
    if (!next) return;
    void fetch(apiUrl('/v1/reports'), { credentials: 'same-origin' })
      .then(async (response) => {
        if (!response.ok) throw new Error('no');
        const body = (await response.json()) as { rows?: ReportRow[] };
        setRows(body.rows ?? []);
      })
      .catch(() => setRows([]));
  };
  const now = Date.now();
  const list = (rows ?? []).filter((row) => !isSample(row));
  const todayRows = list.filter((row) => rowDay(row) === dayKey(now));
  const monthRows = list.filter((row) => inMonth(row, now));
  const monthStat = tally(monthRows, 'all', now);
  const books = byBook(monthRows).filter((item) => item.name).slice(0, 5).map((item) => ({ name: item.name, n: item.taken }));
  const recent = [...monthRows].sort((a, b) => Date.parse(b.at) - Date.parse(a.at)).slice(0, 3);
  return (
    <div className="nx-rep__dock">
      {open && (
        <div className="nx-rep__dockpanel">
          <div className="nx-rep__docknums">
            <p><b>{rows === null ? '…' : moneyOf(todayRows)}</b><span>сегодня</span></p>
            <p><b>{rows === null ? '…' : moneyOf(monthRows)}</b><span>этот месяц</span></p>
          </div>
          <p className="nx-rep__dockbits">
            {`взято ${monthStat.taken}`}
            {monthStat.rate !== null ? ` · прошло ${monthStat.rate}%` : ''}
            {monthStat.hit + monthStat.miss > 0 ? ` · зашло ${monthStat.hit}` : ''}
          </p>
          <DockPie parts={books} />
          {recent.length > 0 && (
            <ul className="nx-rep__docklist">
              {recent.map((row) => (
                <li key={row.id}>
                  <span>{tapeLabel(row.tape) ? `${row.match} · ${tapeLabel(row.tape)}` : row.match}</span>
                  <em>{row.mark === 'hit' && row.gainAmount ? moneyLine(row.gainAmount, row.gainCur === 'usd' ? 'usd' : 'rub') : 'ждут'}</em>
                </li>
              ))}
            </ul>
          )}
          <a className="nx-rep__dockgo" href={here ? '#nx-slip' : reportHref()} onClick={() => setOpen(false)}>добавить вилку</a>
        </div>
      )}
      <button type="button" className="nx-rep__dockbtn" aria-label="статистика" aria-expanded={open} onClick={show}>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 20V11" />
          <path d="M10 20V4" />
          <path d="M16 20v-6" />
          <path d="M3 20h18" />
        </svg>
      </button>
    </div>
  );
}
